'use client';

import { useState, FormEvent } from 'react';
import { AlertCircle, Check, Type, Hash, ToggleLeft, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Input, Textarea, Select, Checkbox } from '../ui/form';
import {
  DivisionOrderAttributeTemplateDto,
  CreateDivisionOrderAttributeTemplateRequest,
  UpdateDivisionOrderAttributeTemplateRequest,
} from '../../types';
import {
  useCreateDivisionOrderAttributeTemplate,
  useUpdateDivisionOrderAttributeTemplate,
} from '../../hooks/use-division-order-attribute-templates';

interface DivisionOrderAttributeTemplateFormProps {
  divisionId: string;
  template?: DivisionOrderAttributeTemplateDto | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export function DivisionOrderAttributeTemplateForm({
  divisionId,
  template,
  onSuccess,
  onCancel,
}: DivisionOrderAttributeTemplateFormProps) {
  const isEditing = !!template;
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    jsonPath: template?.jsonPath || '$.',
    label: template?.label || '',
    fieldType: template?.fieldType || 'string',
    description: template?.description || '',
    displayOrder: template?.displayOrder?.toString() || '0',
    validationRules: template?.validationRules || '',
    allowedValuesJsonPath: template?.allowedValuesJsonPath || '',
    showValueInList: template?.showValueInList ?? false,
    isActive: template?.isActive ?? true,
  });

  const createTemplate = useCreateDivisionOrderAttributeTemplate(divisionId);
  const updateTemplate = useUpdateDivisionOrderAttributeTemplate(
    divisionId,
    template?.id || ''
  );

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      setValidationError(null);

      if (!formData.jsonPath.startsWith('$.')) {
        setValidationError('JSON path must start with $.');
        return;
      }
      if (!formData.label.trim()) {
        setValidationError('Label is required');
        return;
      }
      if (formData.validationRules) {
        try {
          JSON.parse(formData.validationRules);
        } catch {
          setValidationError('Validation rules must be valid JSON');
          return;
        }
      }

      if (isEditing && template?.id) {
        const req: UpdateDivisionOrderAttributeTemplateRequest = {
          jsonPath: formData.jsonPath,
          label: formData.label,
          fieldType: formData.fieldType as 'string' | 'number' | 'boolean' | 'date',
          description: formData.description || undefined,
          displayOrder: parseInt(formData.displayOrder, 10),
          validationRules: formData.validationRules || undefined,
          allowedValuesJsonPath: formData.allowedValuesJsonPath || undefined,
          showValueInList: formData.showValueInList,
          isActive: formData.isActive,
        };
        await updateTemplate.mutateAsync(req);
      } else {
        const req: CreateDivisionOrderAttributeTemplateRequest = {
          jsonPath: formData.jsonPath,
          label: formData.label,
          fieldType: formData.fieldType as 'string' | 'number' | 'boolean' | 'date',
          description: formData.description || undefined,
          displayOrder: parseInt(formData.displayOrder, 10),
          validationRules: formData.validationRules || undefined,
          allowedValuesJsonPath: formData.allowedValuesJsonPath || undefined,
          showValueInList: formData.showValueInList,
        };
        await createTemplate.mutateAsync(req);
      }

      onSuccess();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'An error occurred';
      setValidationError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldTypeDescriptions: Record<string, string> = {
    string: 'Text input for strings, notes, or short descriptions',
    number: 'Numeric input for amounts, quantities, or counts',
    boolean: 'Toggle switch for yes/no or true/false values',
    date: 'Date picker for selecting dates',
  };

  const examplePaths: Record<string, string> = {
    string: '$.Notes',
    number: '$.Order_Entries[0].QuantityOnOrder',
    boolean: '$.IsRushOrder',
    date: '$.DeliveryDate',
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {validationError && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-800">Validation Error</h4>
            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{validationError}</p>
          </div>
        </div>
      )}

      {/* Info banner */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800">
        <strong>Division Required Attribute</strong> — this attribute will be automatically added
        to every workflow in this division as a required field. Workflow admins can configure
        step ranges and role restrictions but cannot remove it.
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Input
            label="Field Label *"
            value={formData.label}
            onChange={(e) => handleChange('label', e.target.value)}
            placeholder="e.g., GL Account Code, Cost Center"
          />
        </div>

        <div className="col-span-2">
          <Input
            label="JSON Path *"
            value={formData.jsonPath}
            onChange={(e) => handleChange('jsonPath', e.target.value)}
            placeholder="$.FieldName"
            className="font-mono text-sm"
            helperText={`Example for ${formData.fieldType}: ${examplePaths[formData.fieldType]}`}
          />
        </div>

        <div className="col-span-2">
          <Input
            label="Allowed Values JSON Path"
            value={formData.allowedValuesJsonPath}
            onChange={(e) => handleChange('allowedValuesJsonPath', e.target.value)}
            placeholder="$.Order_Attributes[?(@.Name == 'FieldName')].AllowedValues"
            className="font-mono text-sm"
            helperText="Optional: JSONPath to a {Name, Value} array in the order document for a dropdown"
          />
        </div>

        <div>
          <Select
            label="Field Type *"
            value={formData.fieldType}
            onChange={(e) => handleChange('fieldType', e.target.value)}
            helperText={fieldTypeDescriptions[formData.fieldType]}
          >
            <option value="string">
              <Type className="h-4 w-4 inline-block" /> String
            </option>
            <option value="number">
              <Hash className="h-4 w-4 inline-block" /> Number
            </option>
            <option value="boolean">
              <ToggleLeft className="h-4 w-4 inline-block" /> Boolean
            </option>
            <option value="date">
              <Calendar className="h-4 w-4 inline-block" /> Date
            </option>
          </Select>
        </div>

        <div>
          <Input
            label="Display Order"
            type="number"
            min="0"
            value={formData.displayOrder}
            onChange={(e) => handleChange('displayOrder', e.target.value)}
            helperText="Order in which the field appears (0 = first)"
          />
        </div>

        <div className="col-span-2">
          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Optional help text shown to approvers"
            rows={3}
          />
        </div>

        <div className="col-span-2">
          <Textarea
            label="Validation Rules (JSON)"
            value={formData.validationRules}
            onChange={(e) => handleChange('validationRules', e.target.value)}
            placeholder='{"min": 0, "max": 999999, "pattern": "^[A-Z0-9-]+$"}'
            rows={3}
            className="font-mono text-sm"
            helperText="Optional JSON validation rules (min, max, pattern, maxLength)"
          />
        </div>

        <div className="col-span-2 pt-2 border-t space-y-3">
          <Checkbox
            label="Show Value in List — when allowed values are present, display both the name and value (e.g. 'Operations (CC-1001)') in the dropdown instead of just the name"
            checked={formData.showValueInList}
            onChange={(e) => handleChange('showValueInList', e.target.checked)}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              {isEditing ? 'Update Template' : 'Create Template'}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
